<template>
  <div class="report-row-wrap" @contextmenu.prevent="onContextMenu">
    <button
      type="button"
      class="report-row"
      @click="$emit('select', report)"
    >
      <DocumentChartBarIcon class="report-row-icon" />
      <span class="report-row-name">{{ report.displayName }}</span>
      <span v-if="breadcrumb" class="report-row-breadcrumb">{{ breadcrumb }}</span>
      <span v-else class="report-row-refreshed">{{ formatRefreshed(report.lastRefreshedAtUtc) }}</span>
    </button>
    <ReportRowMenu
      ref="menuRef"
      :report="report"
      @view="emit('select', props.report)"
      @toggle-favorite="emit('toggle-favorite', props.report)"
      @download="(fmt) => emit('download', props.report, fmt)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { DocumentChartBarIcon } from "@heroicons/vue/24/solid";
import ReportRowMenu from "@/components/reports/ReportRowMenu.vue";
import type { ReportItem, ReportExportFormat } from "@/types/reports";

const props = defineProps<{
  report: ReportItem;
  breadcrumb?: string;
}>();

const emit = defineEmits<{
  (e: "select", report: ReportItem): void;
  (e: "toggle-favorite", report: ReportItem): void;
  (e: "download", report: ReportItem, format: ReportExportFormat): void;
}>();

const menuRef = ref<InstanceType<typeof ReportRowMenu> | null>(null);

function onContextMenu(e: MouseEvent) {
  menuRef.value?.openAt(e.clientX, e.clientY);
}

function formatRefreshed(val?: string): string {
  if (!val) return "---";
  // EF returns DateTime without a timezone indicator; append Z so the browser
  // always parses it as UTC instead of local time.
  const utcVal = /Z|[+-]\d{2}:?\d{2}$/.test(val) ? val : val + "Z";
  const d = new Date(utcVal);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return "---";

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const date = `${get("month")}/${get("day")}/${get("year")}`;
  const time = `${get("hour")}:${get("minute")}:${get("second")}${get("dayPeriod").toLowerCase()}`;
  return `${date} | ${time}`;
}
</script>

<style scoped>
.report-row-wrap {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

.report-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  padding: 10px 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #1a4d8f;
  text-align: left;
  min-width: 0;
}

.report-row:hover {
  background: #f7f9fc;
}

.report-row-icon {
  width: 16px;
  height: 16px;
  color: #2f8a5b;
  flex-shrink: 0;
}

.report-row-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-row-refreshed {
  width: 148px;
  text-align: left;
  flex-shrink: 0;
  color: #888;
  font-size: 12px;
}

.report-row-breadcrumb {
  width: 220px;
  text-align: right;
  flex-shrink: 0;
  color: #888;
  font-size: 12px;
  padding-right: 8px;
}
</style>
